<script setup>
import { computed, onMounted, ref } from 'vue';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';

const { accountScopedRoute } = useAccount();
const conversations = ref([]);
const isLoading = ref(false);

const qualityLabels = {
  good: 'Boa',
  review: 'Revisar',
  critical: 'Crítica',
};

const reasonLabels = {
  complex_request: 'Solicitação complexa',
  commercial_intent: 'Intenção comercial',
  customer_requested_human: 'Cliente pediu humano',
  low_confidence: 'Baixa confiança da IA',
  policy_or_sensitive: 'Caso sensível',
};

const copy = {
  title: 'Supervisão IA',
  description: 'Acompanhamento das conversas conduzidas por agentes de IA.',
  active: 'IA ativa',
  review: 'Precisa revisar',
  withoutQuality: 'Sem qualidade',
  mappedReasons: 'Motivos mapeados',
  activeConversations: 'Conversas em atendimento IA',
  emptyConversations: 'Nenhuma conversa ativa com IA agora.',
  transferReasons: 'Motivos de transferência',
  emptyReasons: 'Ainda não há motivos registrados.',
  loadError: 'Não foi possível carregar as conversas em atendimento pela IA.',
};

const fetchAiConversations = async () => {
  if (isLoading.value) return;

  isLoading.value = true;
  try {
    const allConversations = [];
    let page = 1;

    while (true) {
      const { data } = await ConversationAPI.get({
        conversationType: 'ai',
        status: 'open',
        assigneeType: 'all',
        sortBy: 'last_activity_at_desc',
        page,
      });
      const responseData = data?.data || {};
      const payload = Array.isArray(responseData.payload)
        ? responseData.payload
        : [];
      const total = Number(responseData.meta?.all_count ?? payload.length);

      allConversations.push(...payload);

      if (!payload.length || allConversations.length >= total) break;
      page += 1;
    }

    conversations.value = allConversations;
  } catch (error) {
    useAlert(copy.loadError);
  } finally {
    isLoading.value = false;
  }
};

const needsReview = computed(() =>
  conversations.value.filter(conversation =>
    ['review', 'critical'].includes(conversation.custom_attributes?.ai_quality)
  )
);

const withoutQuality = computed(
  () =>
    conversations.value.filter(
      conversation => !conversation.custom_attributes?.ai_quality
    ).length
);

const transferReasons = computed(() => {
  return conversations.value.reduce((acc, conversation) => {
    const reason = conversation.custom_attributes?.ai_transfer_reason;
    if (!reason) return acc;
    acc[reason] = (acc[reason] || 0) + 1;
    return acc;
  }, {});
});

const reasonRows = computed(() =>
  Object.entries(transferReasons.value).map(([reason, total]) => ({
    reason,
    total,
    label: reasonLabels[reason] || reason,
  }))
);

const formatTimestamp = value => {
  if (!value) return '-';
  const timestamp = String(value).length === 10 ? Number(value) * 1000 : value;
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(timestamp));
};

const conversationRoute = conversation =>
  accountScopedRoute('conversation_through_ai', {
    conversationId: conversation.id,
  });

onMounted(fetchAiConversations);
</script>

<template>
  <section
    class="flex h-full min-w-0 flex-col overflow-y-auto bg-n-background p-6"
  >
    <div class="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 class="m-0 text-2xl font-semibold text-n-slate-12">
          {{ copy.title }}
        </h1>
        <p class="m-0 mt-1 text-sm text-n-slate-11">
          {{ copy.description }}
        </p>
      </div>
      <Button
        label="Atualizar"
        icon="i-lucide-refresh-cw"
        slate
        faded
        :is-loading="isLoading"
        @click="fetchAiConversations"
      />
    </div>

    <div class="grid gap-3 md:grid-cols-4">
      <div class="rounded-lg border border-n-weak bg-n-solid-1 p-4">
        <div class="text-xs font-medium text-n-slate-10">
          {{ copy.active }}
        </div>
        <div class="mt-2 text-2xl font-semibold text-n-slate-12">
          {{ conversations.length }}
        </div>
      </div>
      <div class="rounded-lg border border-n-weak bg-n-solid-1 p-4">
        <div class="text-xs font-medium text-n-slate-10">
          {{ copy.review }}
        </div>
        <div class="mt-2 text-2xl font-semibold text-n-ruby-10">
          {{ needsReview.length }}
        </div>
      </div>
      <div class="rounded-lg border border-n-weak bg-n-solid-1 p-4">
        <div class="text-xs font-medium text-n-slate-10">
          {{ copy.withoutQuality }}
        </div>
        <div class="mt-2 text-2xl font-semibold text-n-amber-10">
          {{ withoutQuality }}
        </div>
      </div>
      <div class="rounded-lg border border-n-weak bg-n-solid-1 p-4">
        <div class="text-xs font-medium text-n-slate-10">
          {{ copy.mappedReasons }}
        </div>
        <div class="mt-2 text-2xl font-semibold text-n-teal-10">
          {{ reasonRows.length }}
        </div>
      </div>
    </div>

    <div class="mt-6 grid gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
      <div class="rounded-lg border border-n-weak bg-n-solid-1">
        <div class="border-b border-n-weak px-4 py-3">
          <h2 class="m-0 text-base font-semibold text-n-slate-12">
            {{ copy.activeConversations }}
          </h2>
        </div>
        <div
          v-if="!conversations.length"
          class="p-8 text-center text-sm text-n-slate-11"
        >
          {{ copy.emptyConversations }}
        </div>
        <div v-else class="divide-y divide-n-weak">
          <RouterLink
            v-for="conversation in conversations"
            :key="conversation.id"
            :to="conversationRoute(conversation)"
            class="grid gap-3 px-4 py-3 transition hover:bg-n-alpha-1 md:grid-cols-[minmax(0,1fr)_8rem_8rem_7rem]"
          >
            <div class="min-w-0">
              <div class="truncate text-sm font-medium text-n-slate-12">
                {{ conversation.meta?.sender?.name || `#${conversation.id}` }}
              </div>
              <div class="mt-1 truncate text-xs text-n-slate-10">
                {{ conversation.meta?.assignee?.name || 'Agente IA' }}
              </div>
            </div>
            <div class="text-sm text-n-slate-11">
              {{
                qualityLabels[conversation.custom_attributes?.ai_quality] ||
                'Sem avaliação'
              }}
            </div>
            <div class="truncate text-sm text-n-slate-11">
              {{
                reasonLabels[
                  conversation.custom_attributes?.ai_transfer_reason
                ] || 'Sem motivo'
              }}
            </div>
            <div class="text-right text-xs text-n-slate-10">
              {{ formatTimestamp(conversation.last_activity_at) }}
            </div>
          </RouterLink>
        </div>
      </div>

      <aside class="rounded-lg border border-n-weak bg-n-solid-1 p-4">
        <h2 class="m-0 text-base font-semibold text-n-slate-12">
          {{ copy.transferReasons }}
        </h2>
        <div v-if="!reasonRows.length" class="mt-4 text-sm text-n-slate-11">
          {{ copy.emptyReasons }}
        </div>
        <div v-else class="mt-4 grid gap-3">
          <div
            v-for="row in reasonRows"
            :key="row.reason"
            class="flex items-center justify-between gap-3 rounded-md bg-n-alpha-1 px-3 py-2"
          >
            <span class="truncate text-sm text-n-slate-12">
              {{ row.label }}
            </span>
            <span class="text-sm font-semibold text-n-teal-10">
              {{ row.total }}
            </span>
          </div>
        </div>
      </aside>
    </div>
  </section>
</template>
